import React from "react";
import { ACCOUNT_TYPES, CLIENT_KINDS, STAGES } from "../utils/constants";
import {
  fmtBin,
  fmtDate,
  fmtDayMonth,
  fmtDaysAgo,
  fmtMoney,
  tariffLabel,
} from "../utils/format";

const COLUMNS = [
  { key: "name", label: "Клиент" },
  { key: "inAmoCrm", label: "В АМО" },
  { key: "accountType", label: "Учётка" },
  { key: "clientKind", label: "Тип" },
  { key: "niche", label: "Ниша" },
  { key: "daysLeft", label: "Тариф и осталось" },
  { key: "beta", label: "Бета PRO+" },
  { key: "likes", label: "Лайки", r: true },
  { key: "applications", label: "Заявки / победы", r: true },
  { key: "fromLikes", label: "Из лайков", r: true },
  { key: "contracts", label: "Договоры", r: true },
  { key: "lastLogin", label: "Был в Colibri", r: true },
  { key: "stage", label: "Этап" },
];

export function StageTag({ stage }) {
  const s = STAGES[stage];
  if (!s) return <span className="zero">—</span>;
  return <span className={`tag ${s.cls}`}>{s.label}</span>;
}

export function AccountTag({ type }) {
  if (!type) return <span className="zero">—</span>;
  return (
    <span className={`tag ${type === "client" ? "ok" : ""}`}>
      {ACCOUNT_TYPES[type] || type}
    </span>
  );
}

export function NicheCell({ niche, source }) {
  if (!niche) return <span className="zero">не определена</span>;
  return (
    <span>
      {niche}
      {source === "declared" && <span className="tag xs">со слов</span>}
      {source === "fact" && <span className="tag xs ok">по БИН</span>}
      {source === "interest" && <span className="tag xs violet">по лайкам</span>}
    </span>
  );
}

function TariffCell({ c }) {
  if (!c.tariff) return <span className="zero">—</span>;
  const d = c.daysLeft;
  if (d === null || d === undefined) return <span>{tariffLabel(c)}</span>;
  if (d < 0)
    return (
      <span className="tag acc">
        {c.tariff} · истекла {fmtDate(c.endAt)}
      </span>
    );
  const cls = d <= 7 ? "red" : d > 60 ? "dim" : "";
  return (
    <>
      {d === 0 ? c.tariff : tariffLabel(c)} ·{" "}
      <span className={`days ${cls}`}>{d === 0 ? "сегодня" : `${d} дн.`}</span>
    </>
  );
}

function Pair({ a, b }) {
  const zero = !a && !b;
  return (
    <span className={zero ? "zero" : ""}>
      {a || 0} / {b || 0}
    </span>
  );
}

const PaidClientsTable = ({ items, loading, error, sort, dir, onSort, selectedId, onOpen }) => {
  return (
    <div className="pc-tablebox">
      <table className="pc-table pc-main-table">
        <thead>
          <tr>
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={`sortable ${col.r ? "r" : ""}`}
                onClick={() => onSort(col.key)}
              >
                {col.label}
                {sort === col.key && <span className="arr">{dir === "desc" ? "↓" : "↑"}</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && !items.length ? (
            <tr>
              <td colSpan={COLUMNS.length} className="pc-empty">
                Загрузка…
              </td>
            </tr>
          ) : error ? (
            <tr>
              <td colSpan={COLUMNS.length} className="pc-empty">
                {error}
              </td>
            </tr>
          ) : !items.length ? (
            <tr>
              <td colSpan={COLUMNS.length} className="pc-empty">
                Под эти фильтры никто не подходит. Сбросьте один из фильтров.
              </td>
            </tr>
          ) : (
            items.map((c) => {
              const mine = c.accountType && c.accountType !== "client";
              return (
                <tr
                  key={c.userId}
                  tabIndex={0}
                  className={`${mine ? "mine" : ""} ${selectedId === c.userId ? "selected" : ""}`}
                  onClick={() => onOpen(c.userId)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onOpen(c.userId);
                  }}
                >
                  <td className="who">
                    <b title={c.displayName}>{c.displayName || "Имя не указано"}</b>
                    <small>
                      {c.phone || "—"}
                      {c.firstBin ? (
                        <>
                          {" · "}
                          <span className="mono">{fmtBin(c.firstBin)}</span>
                          {c.extraBins > 0 && ` +${c.extraBins}`}
                        </>
                      ) : (
                        <span className="nobin"> · БИН не указан</span>
                      )}
                    </small>
                  </td>
                  <td>
                    {c.inAmoCrm === true ? (
                      <span className="tag ok">Да (В АМО)</span>
                    ) : c.inAmoCrm === false ? (
                      <span className="tag">Нет</span>
                    ) : (
                      <span className="zero">—</span>
                    )}
                  </td>
                  <td>
                    <AccountTag type={c.accountType} />
                  </td>
                  <td>
                    {c.clientKind ? (
                      CLIENT_KINDS[c.clientKind] || c.clientKind
                    ) : (
                      <span className="zero">—</span>
                    )}
                  </td>
                  <td>
                    <NicheCell niche={c.niche} source={c.nicheSource} />
                  </td>
                  <td>
                    <TariffCell c={c} />
                  </td>
                  <td>
                    {c.betaUntil ? (
                      <span className="tag acc">до {fmtDayMonth(c.betaUntil)}</span>
                    ) : (
                      <span className="zero">—</span>
                    )}
                  </td>
                  <td className={`r ${!c.likes ? "zero" : ""}`}>
                    {(c.likes || 0).toLocaleString("ru-RU")}
                  </td>
                  <td className="r">
                    <Pair a={c.applications} b={c.wins} />
                  </td>
                  <td className="r">
                    <Pair a={c.appsFromLikes} b={c.winsFromLikes} />
                  </td>
                  <td className="r">
                    {c.contracts ? (
                      <>
                        <span className="win">{c.contracts}</span>
                        <br />
                        <small className="zero">{fmtMoney(c.contractsSum)}</small>
                      </>
                    ) : (
                      <span className="zero">0</span>
                    )}
                  </td>
                  <td className="r">{fmtDaysAgo(c.lastLoginDaysAgo)}</td>
                  <td>
                    <StageTag stage={c.stage} />
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

// Значения для локальной сортировки (дублирует серверную, чтобы порядок был стабильным)
export const SORT_GETTERS = {
  name: (c) => (c.displayName || "").toLowerCase(),
  inAmoCrm: (c) => (c.inAmoCrm ? 1 : 0),
  accountType: (c) => c.accountType || "",
  clientKind: (c) => c.clientKind || "",
  niche: (c) => (c.niche || "").toLowerCase(),
  daysLeft: (c) => (c.daysLeft ?? Number.MAX_SAFE_INTEGER),
  beta: (c) => (c.betaUntil ? new Date(c.betaUntil).getTime() : 0),
  likes: (c) => c.likes || 0,
  applications: (c) => c.applications || 0,
  fromLikes: (c) => c.appsFromLikes || 0,
  contracts: (c) => c.contracts || 0,
  lastLogin: (c) => (c.lastLoginDaysAgo ?? Number.MAX_SAFE_INTEGER),
  stage: (c) => c.stage || "",
};

export default PaidClientsTable;
